import { useChains } from "@opensea/app-kit"
import { graphql } from "@opensea/graphql"
import { isAddressEqual } from "@opensea/utils"
import { useEffect, useRef } from "react"
import { useClient as useUrqlClient } from "urql"
import { useAccount } from "../../polychain/hooks"
import type { Address } from "../../types"
import { AccountActions } from "../accountStore"

const query = graphql(`
  query AccountStateByAddressQuery(
    $address: Address!
    $chain: ChainIdentifier
  ) {
    accountStateByAddress(address: $address, chain: $chain) {
      address
      states
    }
  }
`)

export function useLoadAccountStatesForConnectedAccount() {
  const urqlClient = useUrqlClient()

  const account = useAccount()
  const currentAddressRef = useRef<Address | undefined>(undefined)
  const { getChainIdentifier } = useChains()

  useEffect(
    function loadAccountStatesForConnectedAccount() {
      currentAddressRef.current = account.address

      const loadAccountStates = async () => {
        if (!(account.address && account.chainId)) {
          return
        }

        // perform account validation
        const { data, error } = await urqlClient.query(query, {
          address: account.address,
          chain: getChainIdentifier(account.chainId, account.chainArch),
          ttl: 1000 * 60 * 60 * 24, // 24 hours
        })

        // Handle race condition where the account changed before the query completed
        if (currentAddressRef.current !== account.address) {
          return
        }

        // If there is an error don't do anything
        if (error) {
          return
        }

        // handle no account state by address
        if (!data?.accountStateByAddress) {
          // if we can't validate an account, we can't let them connect
          AccountActions.setAccount(undefined)
          return
        }

        // sanity check malicious user behavior and prevent connecting
        const { address: responseAddress, states } = data.accountStateByAddress
        if (!isAddressEqual(account.address, responseAddress)) {
          AccountActions.setAccount(undefined)
          return
        }

        // update states and trigger downstream validation
        AccountActions.setTsStates(states)
      }

      loadAccountStates()
    },
    [
      urqlClient,
      getChainIdentifier,
      account.address,
      account.chainId,
      account.chainArch,
    ],
  )
}
